import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useListTransactions } from '../../hooks/useListTransactions';

const RecentTransactions: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useListTransactions(1); // Always fetch first page for recent transactions

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          Failed to load recent transactions
        </Typography>
      </Box>
    );
  }

  const transactions = data?.results || [];

  return (
    <Card sx={{ 
      backdropFilter: 'blur(20px)',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.1)',
      borderRadius: 3,
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
          Recent Transactions
        </Typography>
        <Box 
          sx={{ 
            mt: 2,
            maxHeight: '400px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          {transactions.map((transaction, index) => (
            <React.Fragment key={transaction.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 2, pr: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: transaction.type === 'DEPOSIT' ? 'success.light' : 'error.light',
                      mr: 2,
                      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {transaction.type === 'DEPOSIT' ? <ArrowUpward /> : <ArrowDownward />}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                      {transaction.merchantName || 'Unknown Merchant'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {new Date(transaction.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: transaction.type === 'DEPOSIT' ? theme.palette.success.main : theme.palette.error.main,
                    fontWeight: 600,
                  }}
                >
                  {transaction.type === 'DEPOSIT' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                </Typography>
              </Box>
              {index < transactions.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecentTransactions; 